// screens/mentor/mentordashboard/Sidebar.js
import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./../../../assets/css/mentor/mentordashboard/Sidebar.css";

/* ════════════════════════════════════════
   ICONS — SVG, 18×18, stroke 1.75
   ════════════════════════════════════════ */
const Icon = ({ children }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const Icons = {
  Dashboard: () => (
    <Icon>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </Icon>
  ),
  Requests: () => (
    <Icon>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="19" y1="8" x2="19" y2="14" />
      <line x1="22" y1="11" x2="16" y2="11" />
    </Icon>
  ),
  Assignments: () => (
    <Icon>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <line x1="10" y1="9" x2="8" y2="9" />
    </Icon>
  ),
  Students: () => (
    <Icon>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </Icon>
  ),
  Questions: () => (
    <Icon>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </Icon>
  ),
  Roadmap: () => (
    <Icon>
      <circle cx="5" cy="6" r="2" />
      <circle cx="19" cy="6" r="2" />
      <circle cx="12" cy="18" r="2" />
      <path d="M7 6h10" />
      <path d="M5 8v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8" />
      <path d="M12 14v2" />
    </Icon>
  ),
  Analytics: () => (
    <Icon>
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </Icon>
  ),
  Enrolled: () => (
    <Icon>
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </Icon>
  ),
  Payment: () => (
    <Icon>
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
    </Icon>
  ),
  Help: () => (
    <Icon>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.18 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 5.78 5.78l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.28 16z" />
    </Icon>
  ),
  Report: () => (
    <Icon>
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <line x1="4" y1="22" x2="4" y2="15" />
    </Icon>
  ),
  Logout: () => (
    <Icon>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </Icon>
  ),
  ChevronLeft: ({ flipped }) => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      style={{
        transform: flipped ? "rotate(180deg)" : "none",
        transition: "transform 0.3s ease",
      }}
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
};

/* ════════════════════════════════════════
   NAV CONFIG
   ════════════════════════════════════════ */
const NAV_ITEMS = [
  {
    section: "Overview",
    items: [
      { path: "/mentor/dashboard", Icon: Icons.Dashboard, label: "Dashboard" },
    ],
  },
  {
    section: "Students",
    items: [
      {
        path: "/mentor/dashboard/students-request",
        Icon: Icons.Requests,
        label: "Students Request",
      },
      {
        path: "/mentor/dashboard/students",
        Icon: Icons.Enrolled,
        label: "Enrolled Students",
      },
      {
        path: "/mentor/dashboard/student-report",
        Icon: Icons.Report,
        label: "Report Student",
      },
    ],
  },
  {
    section: "Academic",
    items: [
      {
        path: "/mentor/dashboard/student-assignments",
        Icon: Icons.Assignments,
        label: "Assignments",
      },
      {
        path: "/mentor/dashboard/student-questions",
        Icon: Icons.Questions,
        label: "Student Questions",
      },
      {
        path: "/mentor/dashboard/roadmap",
        Icon: Icons.Roadmap,
        label: "Roadmap",
      },
    ],
  },
  {
    section: "Insights",
    items: [
      {
        path: "/mentor/dashboard/analytics",
        Icon: Icons.Analytics,
        label: "Analytics",
      },
      {
        path: "/mentor/dashboard/payment-received",
        Icon: Icons.Payment,
        label: "Payment Received",
      },
    ],
  },
  {
    section: "Support",
    items: [
      {
        path: "/mentor/dashboard/help-support",
        Icon: Icons.Help,
        label: "Help & Support",
      },
    ],
  },
];

/* ════════════════════════════════════════
   SIDEBAR COMPONENT
   ════════════════════════════════════════ */
function MentorSidebar({
  collapsed,
  onToggle,
  mentorName,
  mentorAvatar,
  mentorTitle,
  mentorEmail,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) => location.pathname === path;
  const avatarLetter = mentorName ? mentorName[0].toUpperCase() : "M";

  return (
    <aside className={`msb${collapsed ? " msb--collapsed" : ""}`}>
      {/* ── Brand / Toggle ── */}
      <div className="msb__header">
        {!collapsed && <span className="msb__brand">TechMentor Hub</span>}
        <button
          type="button"
          className="msb__toggle"
          onClick={onToggle}
          aria-label="Toggle sidebar"
        >
          <Icons.ChevronLeft flipped={collapsed} />
        </button>
      </div>

      {/* ── Profile ── */}
      <div className="msb__profile">
        <div className="msb__avatar">
          {mentorAvatar ? (
            <img src={mentorAvatar} alt="avatar" className="msb__avatar-img" />
          ) : (
            <span className="msb__avatar-letter">{avatarLetter}</span>
          )}
          <span className="msb__online" />
        </div>
        {!collapsed && (
          <div className="msb__profile-info">
            <span className="msb__profile-name">{mentorName || "Mentor"}</span>
            {/* ── Email show hogi role ki jagah ── */}
            <span className="msb__profile-role" title={mentorEmail}>
              {mentorEmail || mentorTitle || "Mentor"}
            </span>
          </div>
        )}
      </div>

      <div className="msb__divider" />

      {/* ── Navigation ── */}
      <nav className="msb__nav">
        {NAV_ITEMS.map(({ section, items }) => (
          <div key={section} className="msb__group">
            {!collapsed && (
              <span className="msb__section-label">{section}</span>
            )}
            {items.map(({ path, Icon, label }, i) => (
              <Link
                key={path}
                to={path}
                className={`msb__item${isActive(path) ? " msb__item--active" : ""}`}
                style={{ animationDelay: `${i * 30}ms` }}
                title={collapsed ? label : undefined}
              >
                {isActive(path) && <span className="msb__active-bar" />}
                <span className="msb__item-icon">
                  <Icon />
                </span>
                {!collapsed && <span className="msb__item-label">{label}</span>}
              </Link>
            ))}
          </div>
        ))}
      </nav>

      {/* ── Footer / Logout ── */}
      <div className="msb__footer">
        <button
          type="button"
          className="msb__logout"
          onClick={() => navigate("/mentor-auth")}
          title={collapsed ? "Logout" : undefined}
        >
          <span className="msb__item-icon">
            <Icons.Logout />
          </span>
          {!collapsed && <span className="msb__item-label">Logout</span>}
        </button>
      </div>
    </aside>
  );
}

export default MentorSidebar;
